import {
  AgentPresenceMetric,
  AgentPresenceMetricAgent,
  DailyConversationsMetric,
  PendingConversationsMetric,
  QueuedTimeMetric,
  QueueMetric,
  QueueMetrics,
  ResponseTimeMetric,
} from 'lib-dashboard-types';
import { Media } from 'lib-shared-types-channels';

const MEDIA_TYPES: readonly Media[] = [
  'email',
  'interactivechat',
  'messaging',
  'speak',
];

/** Sums two nullable numbers, treating null as absent (not zero). */
const sum = (a: number | null, b: number | null): number | null => {
  if (a === null && b === null) return null;
  return (a ?? 0) + (b ?? 0);
};

/**
 * Aggregates metrics across multiple queues into a single set of metrics. Uses
 * weighted averages for time-based metrics and deduplicates agents.
 */
export const aggregateQueueMetrics = (
  allQueueMetrics: readonly QueueMetrics[],
): QueueMetric[] => {
  if (allQueueMetrics.length === 0) return [];

  const agentPresence: AgentPresenceMetric[] = [];
  const dailyConversations: DailyConversationsMetric[] = [];
  const pendingConversations: PendingConversationsMetric[] = [];
  const queuedTime: QueuedTimeMetric[] = [];
  const responseTime: ResponseTimeMetric[] = [];

  for (const queueMetrics of allQueueMetrics) {
    for (const metric of queueMetrics.metrics) {
      switch (metric.type) {
        case 'agent_presence':
          agentPresence.push(metric);
          break;
        case 'daily_conversations':
          dailyConversations.push(metric);
          break;
        case 'pending_conversations':
          pendingConversations.push(metric);
          break;
        case 'queued_time':
          queuedTime.push(metric);
          break;
        case 'response_time':
          responseTime.push(metric);
          break;
      }
    }
  }

  const result: QueueMetric[] = [];

  if (agentPresence.length > 0) {
    result.push(aggregateAgentPresence(agentPresence));
  }
  if (dailyConversations.length > 0) {
    result.push(aggregateDailyConversations(dailyConversations));
  }
  if (pendingConversations.length > 0) {
    result.push(aggregatePendingConversations(pendingConversations));
  }
  if (queuedTime.length > 0) {
    result.push(aggregateQueuedTime(queuedTime));
  }
  if (responseTime.length > 0) {
    result.push(aggregateResponseTime(responseTime));
  }

  return result;
};

/**
 * Deduplicates agents across queues by agent_id. An agent is considered
 * online/available if they appear in ANY queue.
 */
function aggregateAgentPresence(
  metrics: AgentPresenceMetric[],
): AgentPresenceMetric {
  const agentMap = new Map<string, AgentPresenceMetricAgent>();

  for (const m of metrics) {
    for (const agent of m.agents) {
      const existing = agentMap.get(agent.agent_id);

      if (!existing) {
        agentMap.set(agent.agent_id, agent);
      } else {
        // Merge: agent is online/available if they are in ANY queue
        agentMap.set(agent.agent_id, {
          agent_id: agent.agent_id,
          online: existing.online || agent.online,
          available: existing.available || agent.available,
          simultaneous_ring:
            existing.simultaneous_ring || agent.simultaneous_ring,
          media: Object.fromEntries(
            MEDIA_TYPES.map((m) => [
              m,
              {
                online: existing.media[m]?.online || agent.media[m]?.online,
                available:
                  existing.media[m]?.available || agent.media[m]?.available,
              },
            ]),
          ) as Record<Media, Record<'online' | 'available', boolean>>,
        });
      }
    }
  }

  return {
    type: 'agent_presence',
    agents: Array.from(agentMap.values()),
  };
}

/**
 * Sums assignment_time_count, assignment_time_sum, total_answered,
 * total_received, and total_abandoned across queues. Computes a weighted
 * average assignment time from the aggregated count and sum.
 */
function aggregateDailyConversations(
  metrics: DailyConversationsMetric[],
): DailyConversationsMetric {
  let assignmentTimeCount: number | null = null;
  let assignmentTimeSum: number | null = null;
  let totalAnswered = 0;
  let totalReceived = 0;
  let totalAbandoned: number | null = null;

  for (const m of metrics) {
    assignmentTimeCount = sum(assignmentTimeCount, m.assignment_time_count);
    assignmentTimeSum = sum(assignmentTimeSum, m.assignment_time_sum);
    totalAnswered += m.total_answered;
    totalReceived += m.total_received;
    totalAbandoned = sum(totalAbandoned, m.total_abandoned);
  }

  const averageAssignmentTime =
    assignmentTimeCount !== null &&
    assignmentTimeCount > 0 &&
    assignmentTimeSum !== null
      ? assignmentTimeSum / assignmentTimeCount
      : null;

  return {
    type: 'daily_conversations',
    assignment_time_count: assignmentTimeCount,
    assignment_time_sum: assignmentTimeSum,
    average_assignment_time: averageAssignmentTime,
    total_answered: totalAnswered,
    total_received: totalReceived,
    total_abandoned: totalAbandoned,
  };
}

/**
 * Sums in_queue, open, follow_up, and total across queues. Null fields are
 * treated as absent — a field is only null if ALL queues report null.
 */
function aggregatePendingConversations(
  metrics: PendingConversationsMetric[],
): PendingConversationsMetric {
  let inQueue: number | null = null;
  let open: number | null = null;
  let followUp: number | null = null;
  let total: number | null = null;

  for (const m of metrics) {
    inQueue = sum(inQueue, m.in_queue);
    open = sum(open, m.open);
    followUp = sum(followUp, m.follow_up);
    total = sum(total, m.total);
  }

  return {
    type: 'pending_conversations',
    in_queue: inQueue,
    open,
    follow_up: followUp,
    total,
  };
}

/**
 * Takes the minimum longest_queued_time (oldest timestamp = longest wait)
 * across queues, and merges all queued conversations sorted by queued_at
 * ascending.
 */
function aggregateQueuedTime(metrics: QueuedTimeMetric[]): QueuedTimeMetric {
  let longestQueuedTime: number | null = null;
  const allConversations: Array<{
    readonly csid: number;
    readonly queued_at: number;
  }> = [];

  for (const m of metrics) {
    if (m.longest_queued_time !== null) {
      if (
        longestQueuedTime === null ||
        m.longest_queued_time < longestQueuedTime
      ) {
        // Lower timestamp = older = longer in queue
        longestQueuedTime = m.longest_queued_time;
      }
    }
    if (m.longest_queued_conversations) {
      allConversations.push(...m.longest_queued_conversations);
    }
  }

  // Sort by queued_at ascending (oldest first = longest waiting)
  allConversations.sort((a, b) => a.queued_at - b.queued_at);

  return {
    type: 'queued_time',
    longest_queued_time: longestQueuedTime,
    longest_queued_conversations:
      allConversations.length > 0 ? allConversations : null,
  };
}

/**
 * Sums response_time_count and response_time_sum across queues. Computes a
 * weighted average response time from the aggregated count and sum.
 */
function aggregateResponseTime(
  metrics: ResponseTimeMetric[],
): ResponseTimeMetric {
  let responseTimeCount: number | null = null;
  let responseTimeSum: number | null = null;

  for (const m of metrics) {
    responseTimeCount = sum(responseTimeCount, m.response_time_count);
    responseTimeSum = sum(responseTimeSum, m.response_time_sum);
  }

  const averageResponseTime =
    responseTimeCount !== null &&
    responseTimeCount > 0 &&
    responseTimeSum !== null
      ? responseTimeSum / responseTimeCount
      : null;

  return {
    type: 'response_time',
    response_time_count: responseTimeCount,
    response_time_sum: responseTimeSum,
    average_response_time: averageResponseTime,
    sla_percentage: null,
  };
}
